import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { sortBy } from "lodash";
import { AgBytesAPI, Ai360API, EventAPI, FieldAPI, RecAPI } from "@ai360/core";

import * as blending from "~/action-panel/components/common/product-blend-modal/blending-utils";
import { DialogBox, DialogBoxFooterType, Loader, SelectInput } from "~/core";
import { actions as picklistActions, selectors as picklistSelectors } from "~/core/picklist";
import { getPickListCode, PICKLIST_CROPPING_SEASON } from "~/core/picklist/picklist-names";
import { getTheUserGuid } from "~/login";
import { models, actions as recEventsActions } from "~/recs-events";
import { batchEditEventsSave } from "~/recs-events/events/actions";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { logFirebaseEvent } from "~/utils/firebase";
import {
    computeIsSelectable,
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
    useProductList,
} from "~/utils/products/use-product-list";
import {
    isCropUnlinked,
    toCropDropdownOptions,
    toCropSelectOptions,
    useCropList,
    useFieldsCropScope,
} from "~/utils/crops/use-crop-list";
import {
    isCropPurposeUnlinked,
    toCropPurposeDropdownOptions,
    toCropPurposeSelectOptions,
    useCropPurposeList,
} from "~/utils/crops/use-crop-purpose-list";
import { messages as globalMessages } from "~/i18n-messages";

import "./batch-edit-events.css";
import { getSelectedEventGuidSet } from "../../selectors";
import { toggleBatchEditEventsModal } from "../../actions";
import { messages } from "../../../i18n-messages";
import WithNewApiResult from "~/hocs/with-api/with-new-api-result";

const MAX_BATCH_EDIT_EVENTS = 500;

const ProductMixTypeOptions = [
    {
        value: EventAPI.BatchEditProductType.SingleProduct,
        label: "Single Product",
    },
    {
        value: EventAPI.BatchEditProductType.CustomBlend,
        label: "Custom Blend",
    },
    {
        value: EventAPI.BatchEditProductType.ChemicalProductMix,
        label: "Chemical Product Mix",
    },
    {
        value: EventAPI.BatchEditProductType.ManureProduct,
        label: "Manure Product",
    },
];

const FilterAttributeMap = {
    [EventAPI.BatchEditFilterAttributeName.MonitorCrop]: "Monitor Crop",
    [EventAPI.BatchEditFilterAttributeName.MonitorProduct]: "Monitor Product",
    [EventAPI.BatchEditFilterAttributeName.MonitorVariety]: "Monitor Variety",
    [EventAPI.BatchEditFilterAttributeName.Product]: "Product",
    [EventAPI.BatchEditFilterAttributeName.VarietyHybrid]: "Variety/Hybrid",
};

const AttributeToUpdateMap = {
    [EventAPI.BatchEditAttributeToUpdate.Crop]: "Crop",
    [EventAPI.BatchEditAttributeToUpdate.ProductMix]: "Product Mix",
    [EventAPI.BatchEditAttributeToUpdate.Season]: "Season",
    [EventAPI.BatchEditAttributeToUpdate.VarietyHybrid]: "Variety/Hybrid",
};

const BatchEditEventsModal = (): ReactElement => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const selectedEventGuidSet = useAppSelector(getSelectedEventGuidSet);
    const userGuid = useAppSelector(getTheUserGuid);
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const seasons = useAppSelector(
        (state) =>
            picklistSelectors.getPicklistOptionsFromCode(
                state,
                getPickListCode(PICKLIST_CROPPING_SEASON)
            ) as FieldAPI.ISeason[]
    );

    const closeModal = () => dispatch(toggleBatchEditEventsModal(false));

    // State for top-level attribute and optional data filter inputs
    const [attributeToUpdate, setAttributeToUpdate] =
        useState<EventAPI.BatchEditAttributeToUpdate>();
    const [existingAttributeFilter, setExistingAttributeFilter] =
        useState<EventAPI.BatchEditFilterAttributeName>(null);
    const [existingValueFilter, setExistingValueFilter] = useState<string>(null);

    // New Attribute Value states
    const [newAttributeSeason, setNewAttributeSeason] = useState<string>(null);
    const [newAttributeCrop, setNewAttributeCrop] = useState<string>(null);
    const [newAttributeCropPurpose, setNewAttributeCropPurpose] = useState<string>(null);
    const [newAttributeBrandOrganization, setNewAttributeBrandOrganization] =
        useState<string>(null);
    const [newAttributeVarietyHybrid, setNewAttributeVarietyHybrid] = useState<string>(null);
    const [newProductType, setNewProductType] = useState<EventAPI.BatchEditProductType>(null);
    const [newProductGuid, setNewProductGuid] = useState<string>(null);

    // State for product mixes
    const [manureProducts, setManureProducts] = useState([]);
    const [chemicalProducts, setChemicalProducts] = useState([]);
    const [fertilizerProducts, setFertilizerProducts] = useState([]);

    const [previewUpdateModalOpen, setPreviewUpdateModalOpen] = useState(false);
    const [updateRequestBody, setUpdateRequestBody] = useState<EventAPI.BatchEditRequest>(null);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        dispatch(Ai360API.util.invalidateTags(["BatchEditEvents"]));
    }, [selectedEventGuidSet, dispatch]);

    const [
        eligibleEvents,
        eventType,
        validAttributesToUpdate,
        errorMessages,
        existingAttributeOptions,
    ] = useMemo(() => {
        const events: models.AgEventSummary[] = [];
        const types = new Set<string>();
        selectedEventGuidSet.forEach((guid) => {
            const selectedEvent = agEventSummaryMap.get(guid);
            // In the case of imported events with a manual event (Planting + Application) added, the event type starts with the imported event type.
            const agEventType = selectedEvent.agEventTypeName.split(" + ")[0];
            if (
                selectedEvent &&
                [
                    models.EVENT_TYPE_NAME_APPLICATION,
                    models.EVENT_TYPE_NAME_HARVEST,
                    models.EVENT_TYPE_NAME_PLANTING,
                ].includes(agEventType) &&
                selectedEvent.isImportedYn
            ) {
                events.push(selectedEvent);
                types.add(agEventType);
            }
        });
        const attributes: EventAPI.BatchEditAttributeToUpdate[] = [
            EventAPI.BatchEditAttributeToUpdate.Season,
        ];
        const existingAttributeOptions: EventAPI.BatchEditFilterAttributeName[] = [];
        switch (Array.from(types)[0]) {
            case models.EVENT_TYPE_NAME_APPLICATION:
                attributes.push(EventAPI.BatchEditAttributeToUpdate.ProductMix);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorProduct);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.Product);
                break;
            case models.EVENT_TYPE_NAME_HARVEST:
                attributes.push(EventAPI.BatchEditAttributeToUpdate.Crop);
                attributes.push(EventAPI.BatchEditAttributeToUpdate.VarietyHybrid);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorCrop);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorVariety);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.VarietyHybrid);
                break;
            case models.EVENT_TYPE_NAME_PLANTING:
                attributes.push(EventAPI.BatchEditAttributeToUpdate.VarietyHybrid);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorCrop);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.MonitorVariety);
                existingAttributeOptions.push(EventAPI.BatchEditFilterAttributeName.VarietyHybrid);
                break;
        }
        const attributeOptions = attributes.map((v) => ({
            value: v,
            label: AttributeToUpdateMap[v],
        }));
        const errorMessages: string[] = [];
        if (events.length > MAX_BATCH_EDIT_EVENTS) {
            errorMessages.push(
                formatMessage(messages.batchEditEventsSelectionLimitWarning, {
                    limit: MAX_BATCH_EDIT_EVENTS,
                })
            );
        }
        if (types.size > 1) {
            errorMessages.push(formatMessage(messages.batchEditEventsSameEventTypeWarning));
        }
        return [
            events,
            Array.from(types)[0],
            attributeOptions,
            errorMessages,
            existingAttributeOptions,
        ];
    }, [selectedEventGuidSet, agEventSummaryMap, dispatch]);

    const productScopeOrgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { products: allProducts, isFetching: isProductListLoading } = useProductList({
        isEnabled: eventType === models.EVENT_TYPE_NAME_APPLICATION,
        orgLevelGuid: productScopeOrgLevelGuid,
        scope: AgBytesAPI.ProductSelectionListScope.UserHierarchy,
    });

    const cropScope = useFieldsCropScope(eligibleEvents.map((event) => event.fieldGuid));
    const { crops } = useCropList({
        scope: cropScope,
        isEnabled:
            eventType === models.EVENT_TYPE_NAME_HARVEST ||
            eventType === models.EVENT_TYPE_NAME_PLANTING,
    });
    const cropData = useMemo(() => toCropDropdownOptions(crops), [crops]);

    const { cropPurposes } = useCropPurposeList({
        cropGuid: newAttributeCrop,
        isEnabled:
            attributeToUpdate === EventAPI.BatchEditAttributeToUpdate.Crop ||
            attributeToUpdate === EventAPI.BatchEditAttributeToUpdate.VarietyHybrid,
        scope: cropScope,
    });
    const cropPurposeData = useMemo(
        () => toCropPurposeDropdownOptions(cropPurposes),
        [cropPurposes]
    );

    const isUnlinkedProduct = useCallback(
        (productType: EventAPI.BatchEditProductType, productGuid: string): boolean =>
            productType === EventAPI.BatchEditProductType.SingleProduct &&
            blending.isProductUnlinked(productGuid, allProducts),
        [allProducts]
    );

    const isSelectedProductUnlinked = isUnlinkedProduct(newProductType, newProductGuid);

    const productNotLinkedText = isSelectedProductUnlinked
        ? formatMessage(globalMessages.productNotLinkedInAgvance)
        : undefined;

    const isRequestProductUnlinked = useCallback(
        (request: EventAPI.BatchEditRequest | null): boolean =>
            Boolean(request && isUnlinkedProduct(request.productType, request.productGuid)),
        [isUnlinkedProduct]
    );

    const isUnlinkedCrop = useCallback(
        (attribute: EventAPI.BatchEditAttributeToUpdate, cropGuid: string): boolean => {
            if (
                attribute !== EventAPI.BatchEditAttributeToUpdate.Crop &&
                attribute !== EventAPI.BatchEditAttributeToUpdate.VarietyHybrid
            ) {
                return false;
            }
            return isCropUnlinked(cropGuid, cropData);
        },
        [cropData]
    );

    const isSelectedCropUnlinked = isUnlinkedCrop(attributeToUpdate, newAttributeCrop);

    const cropNotLinkedText = isSelectedCropUnlinked
        ? formatMessage(globalMessages.cropNotLinkedInAgvance)
        : undefined;

    const isRequestCropUnlinked = useCallback(
        (request: EventAPI.BatchEditRequest | null): boolean =>
            Boolean(request && isUnlinkedCrop(request.attributeToUpdate, request.cropGuid)),
        [isUnlinkedCrop]
    );

    const isUnlinkedCropPurpose = useCallback(
        (attribute: EventAPI.BatchEditAttributeToUpdate, cropPurposeGuid: string): boolean => {
            if (
                attribute !== EventAPI.BatchEditAttributeToUpdate.Crop &&
                attribute !== EventAPI.BatchEditAttributeToUpdate.VarietyHybrid
            ) {
                return false;
            }
            return isCropPurposeUnlinked(cropPurposeGuid, cropPurposeData);
        },
        [cropPurposeData]
    );

    const isSelectedCropPurposeUnlinked = isUnlinkedCropPurpose(
        attributeToUpdate,
        newAttributeCropPurpose
    );

    const cropPurposeNotLinkedText = isSelectedCropPurposeUnlinked
        ? formatMessage(globalMessages.cropPurposeNotLinkedInAgvance)
        : undefined;

    const isRequestCropPurposeUnlinked = useCallback(
        (request: EventAPI.BatchEditRequest | null): boolean =>
            Boolean(
                request && isUnlinkedCropPurpose(request.attributeToUpdate, request.cropPurposeGuid)
            ),
        [isUnlinkedCropPurpose]
    );

    useEffect(() => {
        if (
            previewUpdateModalOpen &&
            (isRequestProductUnlinked(updateRequestBody) ||
                isRequestCropUnlinked(updateRequestBody) ||
                isRequestCropPurposeUnlinked(updateRequestBody))
        ) {
            setUpdateRequestBody(null);
            setPreviewUpdateModalOpen(false);
        }
    }, [
        previewUpdateModalOpen,
        updateRequestBody,
        isRequestProductUnlinked,
        isRequestCropUnlinked,
        isRequestCropPurposeUnlinked,
    ]);

    const { data: filterData, isLoading: filterDataIsLoading } =
        EventAPI.useGetBatchEditFilterValuesQuery(
            {
                eventGeneral: eligibleEvents.map((event) => event.agEventGeneralGuid),
                attributeName: existingAttributeFilter,
            },
            {
                skip: !existingAttributeFilter || selectedEventGuidSet.size === 0,
            }
        );

    const [
        filteredEventsTrigger,
        {
            data: filteredEventsData,
            isLoading: filteredEventsIsLoading,
            isFetching: filteredEventsIsFetching,
        },
    ] = EventAPI.useLazyGetBatchEditFilteredEventsQuery();

    const [batchEditUpdate, { isLoading: batchEditUpdateIsLoading }] =
        EventAPI.useBatchEditEventsMutation();

    const {
        data: brandOrgData = { model: [] },
        isLoading: brandOrgDataIsLoading,
        isFetching: brandOrgDataIsFetching,
    } = AgBytesAPI.useGetBrandOrganizationCropListQuery(newAttributeCrop, {
        skip: !newAttributeCrop,
    });

    const getVarietyHybridQueryArgs = useMemo(() => {
        return {
            CropID: newAttributeCrop,
            BrandOrganization: "",
        };
    }, [newAttributeCrop]);

    const {
        data: varietyHybridData = { model: [] },
        isLoading: varietyHybridDataIsLoading,
        isFetching: varietyHybridDataIsFetching,
    } = AgBytesAPI.useGetVarietyHybridFilterListQuery(getVarietyHybridQueryArgs, {
        skip: !newAttributeCrop,
    });

    const varietyHybridOptions = useMemo(() => {
        const options = newAttributeBrandOrganization
            ? varietyHybridData.model
                  .filter((v) => v.parentGuid === newAttributeBrandOrganization)
                  .map(({ guid, name }) => ({
                      value: guid,
                      label: name,
                  }))
            : varietyHybridData.model.map(({ guid, name }) => ({
                  value: guid,
                  label: name,
              }));
        return sortBy(options, (x) => x.label);
    }, [varietyHybridData.model, newAttributeBrandOrganization]);

    const croppingSeasonRequestMade = useRef(false);

    useEffect(() => {
        if ((!seasons || seasons.length === 0) && !croppingSeasonRequestMade.current) {
            croppingSeasonRequestMade.current = true;
            dispatch(
                picklistActions.fetchPicklistData({
                    [PICKLIST_CROPPING_SEASON]: getPickListCode(PICKLIST_CROPPING_SEASON),
                })
            );
        }
    }, [seasons, dispatch]);

    useEffect(() => {
        const fetchProductData = async () => {
            const getChemicalAndFertilizerProducts = async (
                typesResponse: RecAPI.IGetProductTypesResponse
            ) => {
                const chemicalProductMixType = typesResponse.productMixTypes.find(
                    (type) => type.name === "Chemical"
                );
                const fertilizerProductMixType = typesResponse.productMixTypes.find(
                    (type) => type.name === "Fertilizer"
                );
                return await Promise.all([
                    chemicalProductMixType
                        ? RecAPI.getProductBlends(
                              userGuid,
                              chemicalProductMixType.productMixTypeGuid
                          )
                        : [],
                    fertilizerProductMixType
                        ? RecAPI.getProductBlends(
                              userGuid,
                              fertilizerProductMixType.productMixTypeGuid
                          )
                        : [],
                ]);
            };
            const promises = [
                RecAPI.getCustomProducts(userGuid, {
                    customProductGuid: "",
                    physicalStateGuid: "",
                    customProductTypeGuid: "",
                    eventRecCustomProductGuids: [],
                }),
                RecAPI.getProductMixTypes(userGuid).then((typesResponse) =>
                    getChemicalAndFertilizerProducts(typesResponse)
                ),
            ];
            setIsLoading(true);
            const [manureProductMixes, [chemicalMixes, fertilizerBlends]] = await Promise.all(
                promises
            );

            setManureProducts(manureProductMixes.sort(blending.sortCustomProducts));
            setChemicalProducts(chemicalMixes);
            setFertilizerProducts(fertilizerBlends);

            setIsLoading(false);
        };
        if (eventType === models.EVENT_TYPE_NAME_APPLICATION) {
            fetchProductData();
        }
    }, [dispatch, eventType, userGuid]);

    if (errorMessages.length > 0) {
        return (
            <DialogBox
                footerType={DialogBoxFooterType.NONE}
                isOpen={true}
                onClose={closeModal}
                title={formatMessage(messages.batchEditEventsWarningTitle)}
                testId="batch-edit-events-warning-modal"
            >
                {errorMessages.map((message, index) => (
                    <div key={index}>{message}</div>
                ))}
            </DialogBox>
        );
    }

    const onPreviewUpdate = async () => {
        logFirebaseEvent("batch_edit_events");
        const updatedAttributes: EventAPI.BatchEditRequest = {
            eventGeneral: eligibleEvents.map((event) => event.agEventGeneralGuid),
            attributeToUpdate,
            croppingSeasonGuid: newAttributeSeason,
            cropGuid: newAttributeCrop,
            cropPurposeGuid: newAttributeCropPurpose,
            brandOrganizationGuid: newAttributeBrandOrganization,
            varietyHybridGuid: newAttributeVarietyHybrid,
            productType: newProductType,
            productGuid: newProductGuid,
        };

        if (existingAttributeFilter) {
            const filterDataItem = filterData.find(
                (item) => item.id === existingValueFilter || item.value === existingValueFilter
            );
            if (filterDataItem) {
                updatedAttributes.filterAttributeName = existingAttributeFilter;
                updatedAttributes.filterValue = filterDataItem.value;
                updatedAttributes.filterGuid = filterDataItem.id;
            }
        }

        setUpdateRequestBody(updatedAttributes);
        setPreviewUpdateModalOpen(true);
        filteredEventsTrigger(updatedAttributes);
    };

    const onCancelUpdate = async () => {
        setUpdateRequestBody(null);
        setPreviewUpdateModalOpen(false);
    };

    const onUpdate = async () => {
        if (
            isRequestProductUnlinked(updateRequestBody) ||
            isRequestCropUnlinked(updateRequestBody) ||
            isRequestCropPurposeUnlinked(updateRequestBody)
        ) {
            setUpdateRequestBody(null);
            setPreviewUpdateModalOpen(false);
            return;
        }
        setIsLoading(true);
        setUpdateRequestBody(null);
        setPreviewUpdateModalOpen(false);
        await batchEditUpdate(updateRequestBody)
            .unwrap()
            .then(() => {
                setNewAttributeSeason(null);
                setNewAttributeCrop(null);
                setNewAttributeCropPurpose(null);
                setNewAttributeBrandOrganization(null);
                setNewAttributeVarietyHybrid(null);
                setNewProductType(null);
                setNewProductGuid(null);
                dispatch(recEventsActions.refreshFilterOptions());
                dispatch(
                    batchEditEventsSave(
                        eligibleEvents.map((e) => ({
                            fieldGuid: e.fieldGuid,
                            agEventGeneralGuid: e.agEventGeneralGuid,
                        })),
                        attributeToUpdate
                    )
                );
            })
            .catch(() => {
                // Handled by WithNewApiResult
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const renderExistingAttributeFilter = () => {
        if (
            !attributeToUpdate ||
            attributeToUpdate === EventAPI.BatchEditAttributeToUpdate.Season
        ) {
            return null;
        }
        return (
            <div className="batch-edit-events-group">
                <div className="batch-edit-events-group-header">
                    {formatMessage(messages.batchEditEventsDataFilters)}
                </div>
                <SelectInput
                    placeholderText={formatMessage(messages.batchEditEventsExistingAttributeFilter)}
                    options={existingAttributeOptions.map((s) => ({
                        value: s,
                        label: FilterAttributeMap[s],
                    }))}
                    value={existingAttributeFilter}
                    onChange={(value) => {
                        setExistingAttributeFilter(value);
                        setExistingValueFilter(null);
                    }}
                    testId="batch-edit-events-existing-attribute-filter-select"
                />
                <SelectInput
                    placeholderText={formatMessage(messages.batchEditEventsExistingValueFilter)}
                    // Options for this should be based on monitor names from the selected events
                    options={sortBy(
                        (filterData || []).map((s) => ({
                            value: s.id || s.value,
                            label: s.value,
                        })),
                        (x) => x.label
                    )}
                    value={existingValueFilter}
                    onChange={(value) => setExistingValueFilter(value)}
                    disabled={!existingAttributeFilter}
                    testId="batch-edit-events-existing-value-filter-select"
                />
            </div>
        );
    };

    const renderProductMixDropdown = () => {
        if (!newProductType) {
            return null;
        }
        const options = [];
        let placeholder = "";
        switch (newProductType) {
            case EventAPI.BatchEditProductType.SingleProduct:
                allProducts.forEach((product) => {
                    options.push({
                        activeYn: product.activeYn,
                        isSelectable: computeIsSelectable(
                            product.activeYn,
                            product.isAgvanceLinked
                        ),
                        value: product.productGuid,
                        label: product.name,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixSingleProduct);
                break;
            case EventAPI.BatchEditProductType.ManureProduct:
                manureProducts.forEach((product) => {
                    options.push({
                        value: product.customProductGuid,
                        label: `${product.customProductType} - ${product.guaranteedAnalysis} (${product.physicalState})`,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixManureProduct);
                break;
            case EventAPI.BatchEditProductType.CustomBlend:
                fertilizerProducts.forEach((product) => {
                    options.push({
                        value: product.productBlendGuid,
                        label: product.name,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixCustomBlend);
                break;
            case EventAPI.BatchEditProductType.ChemicalProductMix:
                chemicalProducts.forEach((product) => {
                    options.push({
                        value: product.productBlendGuid,
                        label: product.name,
                    });
                });
                placeholder = formatMessage(messages.batchEditEventsProductMixChemicalProductMix);
                break;
        }
        return (
            <SelectInput
                key={EventAPI.BatchEditAttributeToUpdate.ProductMix + "1"}
                clearable={false}
                hasError={isSelectedProductUnlinked}
                // Only the SingleProduct branch's options carry isSelectable -- manure/fertilizer/
                // chemical options have no such key and would all be hidden if applied unconditionally.
                optionIsHiddenKey={
                    newProductType === EventAPI.BatchEditProductType.SingleProduct
                        ? IS_SELECTABLE
                        : null
                }
                placeholderText={placeholder}
                options={options}
                toolTip={productNotLinkedText}
                value={newProductGuid}
                onChange={(value) => setNewProductGuid(value)}
                disabled={!newProductType}
                testId="batch-edit-events-new-product-select"
            />
        );
    };

    const renderNewAttributeValues = () => {
        const selectInputs = [];
        switch (attributeToUpdate) {
            case EventAPI.BatchEditAttributeToUpdate.Season:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.Season}
                        placeholderText={formatMessage(messages.batchEditEventsSeason)}
                        options={seasons}
                        value={newAttributeSeason}
                        clearable={false}
                        required
                        onChange={(value) => setNewAttributeSeason(value)}
                        testId="batch-edit-events-season-select"
                    />
                );
                break;
            case EventAPI.BatchEditAttributeToUpdate.Crop:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.Crop}
                        placeholderText={formatMessage(messages.batchEditEventsCrop)}
                        hasError={isSelectedCropUnlinked}
                        optionIsHiddenKey={IS_SELECTABLE}
                        options={sortBy(
                            toCropSelectOptions(cropData, formatMessage),
                            (x) => x.label
                        )}
                        toolTip={cropNotLinkedText}
                        value={newAttributeCrop}
                        clearable={false}
                        onChange={(value) => {
                            setNewAttributeBrandOrganization(null);
                            setNewAttributeVarietyHybrid(null);
                            setNewAttributeCropPurpose(null);
                            setNewAttributeCrop(value);
                        }}
                        required
                        testId="batch-edit-events-crop-select"
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.Crop + "1"}
                        hasError={isSelectedCropPurposeUnlinked}
                        optionIsHiddenKey={IS_SELECTABLE}
                        placeholderText={formatMessage(messages.batchEditEventsCropPurpose)}
                        options={sortBy(
                            toCropPurposeSelectOptions(cropPurposeData, formatMessage),
                            (x) => x.label
                        )}
                        toolTip={cropPurposeNotLinkedText}
                        value={newAttributeCropPurpose}
                        clearable={true}
                        onChange={setNewAttributeCropPurpose}
                        testId="batch-edit-events-crop-purpose-select"
                    />
                );
                break;
            case EventAPI.BatchEditAttributeToUpdate.VarietyHybrid:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid}
                        placeholderText={formatMessage(messages.batchEditEventsCrop)}
                        hasError={isSelectedCropUnlinked}
                        optionIsHiddenKey={IS_SELECTABLE}
                        options={sortBy(
                            toCropSelectOptions(cropData, formatMessage),
                            (x) => x.label
                        )}
                        toolTip={cropNotLinkedText}
                        value={newAttributeCrop}
                        clearable={false}
                        onChange={(value) => {
                            setNewAttributeBrandOrganization(null);
                            setNewAttributeVarietyHybrid(null);
                            setNewAttributeCrop(value);
                        }}
                        required
                        testId="batch-edit-events-variety-hybrid-crop-select"
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid + "1"}
                        hasError={isSelectedCropPurposeUnlinked}
                        optionIsHiddenKey={IS_SELECTABLE}
                        placeholderText={formatMessage(messages.batchEditEventsCropPurpose)}
                        options={sortBy(
                            toCropPurposeSelectOptions(cropPurposeData, formatMessage),
                            (x) => x.label
                        )}
                        toolTip={cropPurposeNotLinkedText}
                        value={newAttributeCropPurpose}
                        clearable={true}
                        onChange={setNewAttributeCropPurpose}
                        testId="batch-edit-events-variety-hybrid-crop-purpose-select"
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid + "2"}
                        placeholderText={formatMessage(messages.batchEditEventsBrandOrganization)}
                        options={sortBy(
                            brandOrgData.model.map(({ guid, name }) => ({
                                value: guid,
                                label: name,
                            })),
                            (x) => x.label
                        )}
                        value={newAttributeBrandOrganization}
                        onChange={(value) => {
                            if (newAttributeVarietyHybrid) {
                                const variety = varietyHybridData.model.find(
                                    (v) =>
                                        v.parentGuid === value &&
                                        v.guid === newAttributeVarietyHybrid
                                );
                                if (!variety) {
                                    setNewAttributeVarietyHybrid(null);
                                }
                            }
                            setNewAttributeBrandOrganization(value);
                        }}
                        disabled={!newAttributeCrop}
                        clearable={eventType !== models.EVENT_TYPE_NAME_PLANTING}
                        required={eventType === models.EVENT_TYPE_NAME_PLANTING}
                        testId="batch-edit-events-brand-organization-select"
                    />
                );
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.VarietyHybrid + "3"}
                        placeholderText={formatMessage(messages.batchEditEventsVarietyHybrid)}
                        options={varietyHybridOptions}
                        value={newAttributeVarietyHybrid}
                        onChange={(value) => {
                            setNewAttributeVarietyHybrid(value);
                            if (value) {
                                const newBrandOrgValue =
                                    varietyHybridData.model.find((v) => v.guid === value)
                                        ?.parentGuid || null;
                                if (newAttributeBrandOrganization !== newBrandOrgValue) {
                                    setNewAttributeBrandOrganization(newBrandOrgValue);
                                }
                            }
                        }}
                        disabled={!newAttributeCrop}
                        clearable={eventType !== models.EVENT_TYPE_NAME_PLANTING}
                        required={eventType === models.EVENT_TYPE_NAME_PLANTING}
                        testId="batch-edit-events-variety-hybrid-select"
                    />
                );
                break;
            case EventAPI.BatchEditAttributeToUpdate.ProductMix:
                selectInputs.push(
                    <SelectInput
                        key={EventAPI.BatchEditAttributeToUpdate.ProductMix}
                        placeholderText={formatMessage(messages.batchEditEventsProductMixType)}
                        options={ProductMixTypeOptions}
                        value={newProductType}
                        clearable={false}
                        onChange={(value) => {
                            setNewProductType(value);
                            setNewProductGuid(null);
                        }}
                        testId="batch-edit-events-product-mix-type-select"
                    />
                );
                selectInputs.push(renderProductMixDropdown());
                break;
        }
        if (selectInputs.length === 0) {
            return null;
        }
        return (
            <div className="batch-edit-events-group">
                <div className="batch-edit-events-group-header">
                    {formatMessage(messages.batchEditEventsNewAttributeValues)}
                </div>
                {selectInputs}
            </div>
        );
    };

    let onUpdateEnabled = false;
    switch (attributeToUpdate) {
        case EventAPI.BatchEditAttributeToUpdate.Season:
            onUpdateEnabled = !!newAttributeSeason;
            break;
        case EventAPI.BatchEditAttributeToUpdate.Crop:
            onUpdateEnabled =
                !!newAttributeCrop && !isSelectedCropUnlinked && !isSelectedCropPurposeUnlinked;
            break;
        case EventAPI.BatchEditAttributeToUpdate.VarietyHybrid:
            if (eventType === models.EVENT_TYPE_NAME_PLANTING) {
                onUpdateEnabled =
                    !!newAttributeVarietyHybrid &&
                    !isSelectedCropUnlinked &&
                    !isSelectedCropPurposeUnlinked;
            } else {
                onUpdateEnabled =
                    !!newAttributeCrop && !isSelectedCropUnlinked && !isSelectedCropPurposeUnlinked;
            }
            break;
        case EventAPI.BatchEditAttributeToUpdate.ProductMix:
            onUpdateEnabled = !!newProductType && !!newProductGuid && !isSelectedProductUnlinked;
            break;
    }

    const showLoader =
        isLoading ||
        filterDataIsLoading ||
        filteredEventsIsLoading ||
        batchEditUpdateIsLoading ||
        brandOrgDataIsFetching ||
        brandOrgDataIsLoading ||
        varietyHybridDataIsFetching ||
        varietyHybridDataIsLoading ||
        isProductListLoading;

    return (
        <>
            <DialogBox
                action={formatMessage(messages.batchEditEventsUpdate)}
                actionDisabled={!onUpdateEnabled}
                draggable={true}
                unrestricted
                footerType={DialogBoxFooterType.ACTION_CLOSE}
                isOpen={true}
                onAction={onPreviewUpdate}
                onClose={closeModal}
                title={formatMessage(messages.batchEditEventsTitle, {
                    count: eligibleEvents.length,
                })}
                className="batch-edit-events-modal"
                testId="batch-edit-events-modal"
            >
                <div className="batch-edit-form">
                    <div className="batch-edit-events-group">
                        <SelectInput
                            clearable={false}
                            placeholderText={formatMessage(
                                messages.batchEditEventsAttributeToUpdate
                            )}
                            options={validAttributesToUpdate}
                            value={attributeToUpdate}
                            onChange={(value: EventAPI.BatchEditAttributeToUpdate) =>
                                setAttributeToUpdate(value)
                            }
                            testId="batch-edit-events-attribute-to-update-select"
                        />
                    </div>
                    {renderExistingAttributeFilter()}
                    {renderNewAttributeValues()}
                </div>
                {showLoader && <Loader className="batch-edit-events-loader" />}
            </DialogBox>
            <DialogBox
                isOpen={
                    previewUpdateModalOpen &&
                    !filteredEventsIsFetching &&
                    !!filteredEventsData &&
                    !isRequestProductUnlinked(updateRequestBody) &&
                    !isRequestCropUnlinked(updateRequestBody) &&
                    !isRequestCropPurposeUnlinked(updateRequestBody)
                }
                isModal={true}
                title={formatMessage(messages.batchEditEventsConfirmTitle)}
                draggable={true}
                hideCloseX={true}
                onAction={onUpdate}
                onClose={onCancelUpdate}
                footerType={DialogBoxFooterType.YES_NO}
                testId="batch-edit-events-confirm-modal"
            >
                {formatMessage(messages.batchEditEventsConfirmText, {
                    count: filteredEventsData?.length,
                })}
            </DialogBox>
        </>
    );
};

export default WithNewApiResult(BatchEditEventsModal);
